import { Position } from '@xyflow/react';
import { LuArrowRight } from 'react-icons/lu';
import BhHandle from '../../../flowkit/handle';
import { BhFlowNode, BhFlowTitle, BhFlowSubtitle } from '../../../flowkit/flowNode';

// A small grab handle on each side. With connectionMode="loose" (set on the canvas) any handle can act as
// both source and target, so a single handle per side is enough to drag a connection from / drop one onto any
// edge of the node. Edges themselves are drawn floating (clipped to the border), not anchored to these.
const HANDLE_SIDES = [
  { id: 'top', position: Position.Top },
  { id: 'right', position: Position.Right },
  { id: 'bottom', position: Position.Bottom },
  { id: 'left', position: Position.Left },
];

/**
 * GraphVertexNode — custom React Flow node for graph vertices, built on the shared flow kit.
 *
 * The bold label (joined labels) and a muted rev line. Small "flexible" grab handles sit on all four sides,
 * hidden until the node is hovered/selected (flowkit.css) so they don't clutter the canvas.
 *
 * In subset mode a frontier vertex (one that may still have unloaded neighbors) is flagged via
 * data.canExpand: the node wrapper is subtly tinted (see index.css) and a small traversal control carrying a
 * → arrow is anchored to the node's bottom-right corner and loads the vertex's neighbors. Double-click on the
 * node body does the same; the arrow control is the explicit affordance so it is obvious which vertices can
 * still be traversed.
 */
function GraphVertexNode({ data, isConnectable, selected }) {
  const rev = data?.element?.rev;
  const hasRev = rev !== undefined && rev !== null;
  const canExpand = !!data.canExpand;
  const handles = HANDLE_SIDES.map(side => (
    <BhHandle
      key={side.id}
      mode="flexible"
      type="source"
      position={side.position}
      id={side.id}
      isConnectable={isConnectable}
    />
  ));
  return (
    <BhFlowNode selected={selected} attachments={handles} className="graph-vertex-node">
      <BhFlowTitle>{data.label}</BhFlowTitle>
      {hasRev && <BhFlowSubtitle tone="muted">rev {rev}</BhFlowSubtitle>}
      {canExpand && (
        <button
          type="button"
          className="graph-vertex-node-expand nodrag nopan"
          title="Load this vertex's neighbors"
          aria-label="Expand neighbors"
          onClick={(event) => {
            event.stopPropagation();
            if (data.onExpand) data.onExpand();
          }}
        >
          <LuArrowRight size={5.76} strokeWidth={2.75} />
        </button>
      )}
    </BhFlowNode>
  );
}

export default GraphVertexNode;
